package fr.manyverse.app.presentation.components.onboarding

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.viewmodel.AuthUiState
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.otp_code_hint
import manyverse.shared.generated.resources.otp_resend_button
import manyverse.shared.generated.resources.otp_sent_to
import manyverse.shared.generated.resources.otp_title
import manyverse.shared.generated.resources.otp_verify_button
import org.jetbrains.compose.resources.stringResource

@Composable
fun OtpVerificationDialog(
    uiState: AuthUiState,
    onOtpCodeChange: (String) -> Unit,
    onVerifyOtp: () -> Unit,
    onResendOtp: () -> Unit,
    onDismiss: () -> Unit
) {
    val focusRequester = remember { FocusRequester() }
    LaunchedEffect(Unit) {
        focusRequester.requestFocus()
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        modifier = Modifier.imePadding().clearFocusOnTap(),
        title = { Text(stringResource(Res.string.otp_title)) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = stringResource(Res.string.otp_sent_to, uiState.email),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.testTag("otpDialogSubtitle")
                )
                OutlinedTextField(
                    value = uiState.otpCode,
                    onValueChange = { input ->
                        if (input.length <= 6 && input.all { it.isDigit() }) {
                            onOtpCodeChange(input)
                        }
                    },
                    label = { Text(stringResource(Res.string.otp_code_hint)) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag("otpDialogCodeField")
                        .focusRequester(focusRequester),
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.NumberPassword,
                        imeAction = ImeAction.Done
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = {
                            if (!uiState.isLoading && uiState.otpCode.length == 6) {
                                onVerifyOtp()
                            }
                        }
                    )
                )
                if (uiState.error != null) {
                    Text(
                        text = stringResource(uiState.error),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                TextButton(
                    onClick = onResendOtp,
                    modifier = Modifier.testTag("resendOtpButton"),
                    enabled = !uiState.isLoading
                ) {
                    Text(
                        text = stringResource(Res.string.otp_resend_button),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
        },
        confirmButton = {
            Button(
                onClick = onVerifyOtp,
                modifier = Modifier.testTag("verifyOtpDialogButton"),
                enabled = !uiState.isLoading && uiState.otpCode.length == 6
            ) {
                if (uiState.isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(16.dp),
                        color = MaterialTheme.colorScheme.onPrimary,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(stringResource(Res.string.otp_verify_button))
                }
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                modifier = Modifier.testTag("cancelOtpDialogButton")
            ) {
                Text(stringResource(Res.string.cancel))
            }
        }
    )
}